import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Clock, Mail, Share2, Globe, FileText, Image, ChevronDown, Filter } from 'lucide-react';

interface Item {
  id: number;
  title: string;
  type: string;
  assets?: any[];
  approval?: {
    am_status?: string;
    client_status?: string;
  };
  asset_approvals?: any[];
}

interface ItemsListProps {
  items: Item[];
  selectedItem: Item | null;
  onItemSelect: (item: Item) => void;
  availablePlans?: any[];
  selectedPlan?: string;
  onPlanChange?: (planId: string) => void;
  reviewType?: 'account_manager' | 'client';
}

function ItemsList({ items, selectedItem, onItemSelect, availablePlans, selectedPlan, onPlanChange, reviewType }: ItemsListProps) {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const getItemIcon = (type: string) => {
    const iconClass = "w-4 h-4";
    switch (type.toLowerCase()) {
      case 'email':
        return <Mail className={iconClass} />;
      case 'social':
        return <Share2 className={iconClass} />;
      case 'website':
      case 'page':
        return <Globe className={iconClass} />;
      case 'design':
        return <Image className={iconClass} />;
      default:
        return <FileText className={iconClass} />;
    }
  };

  const getItemStatus = (item: Item) => {
    // Check if all assets are approved for current reviewer type
    if (reviewType && item.assets && item.assets.length > 0) {
      const allApproved = item.assets.every((asset: any) => {
        const approval = asset.asset_approvals?.find((a: any) => a.approver_type === reviewType);
        return approval?.status === 'approved';
      });

      const anyRejected = item.assets.some((asset: any) => {
        const approval = asset.asset_approvals?.find((a: any) => a.approver_type === reviewType);
        return approval?.status === 'rejected';
      });

      if (allApproved) return 'approved';
      if (anyRejected) return 'rejected';

      const anyInProgress = item.assets.some((asset: any) => {
        const approval = asset.asset_approvals?.find((a: any) => a.approver_type === reviewType);
        return approval !== undefined;
      });

      if (anyInProgress) return 'in_review';
      return 'pending';
    }

    // Fallback to approval status if no reviewer type specified
    if (!item.approval) return 'pending';
    if (item.approval.am_status === 'approved' && item.approval.client_status === 'approved') {
      return 'approved';
    }
    if (item.approval.am_status === 'rejected' || item.approval.client_status === 'rejected') {
      return 'rejected';
    }
    return 'in_review';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejected':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'in_review':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getAssetCounts = (assets: any[] = []) => {
    const counts: Record<string, number> = {};
    assets.forEach(asset => {
      // Transform 'meta' to 'ads' for campaign JSON files
      let displayType = asset.asset_type;
      if (asset.asset_type === 'meta' && asset.file_name?.includes('campaign')) {
        displayType = 'ads';
      }
      counts[displayType] = (counts[displayType] || 0) + 1;
    });
    return counts;
  };

  const getApprovalProgress = (item: Item) => {
    if (!item.assets || item.assets.length === 0) return { approved: 0, total: 0, percentage: 0 };

    let approvedCount = 0;
    const totalAssets = item.assets.length;

    item.assets.forEach(asset => {
      if (reviewType) {
        // Check approval for current reviewer type only
        const approval = asset.asset_approvals?.find((a: any) =>
          a.approver_type === reviewType && a.status === 'approved'
        );
        if (approval) {
          approvedCount++;
        }
      } else {
        // Check if both AM and client have approved this asset (fallback)
        const amApproved = asset.asset_approvals?.find((a: any) =>
          a.approver_type === 'account_manager' && a.status === 'approved'
        );
        const clientApproved = asset.asset_approvals?.find((a: any) =>
          a.approver_type === 'client' && a.status === 'approved'
        );

        if (amApproved && clientApproved) {
          approvedCount++;
        }
      }
    });

    return {
      approved: approvedCount,
      total: totalAssets,
      percentage: totalAssets > 0 ? Math.round((approvedCount / totalAssets) * 100) : 0
    };
  };

  // Filter items based on status and type
  const filteredItems = items.filter(item => {
    const status = getItemStatus(item);
    const matchesStatus = statusFilter === 'all' || status === statusFilter;
    const matchesType = typeFilter === 'all' || item.type.toLowerCase() === typeFilter.toLowerCase();
    return matchesStatus && matchesType;
  });

  // Get unique types from items
  const uniqueTypes = Array.from(new Set(items.map(item => item.type.toLowerCase())));

  return (
    <div className="h-full flex flex-col">
      {/* Header with Plan Dropdown and Filters */}
      <div className="p-3 border-b bg-white">
        {/* Plan Dropdown - Primary Navigation */}
        {availablePlans && availablePlans.length > 0 ? (
          <div className="mb-3">
            <label className="text-xs text-gray-600 mb-1 block font-semibold">Content Plan</label>
            <div className="relative">
              <button
                className="w-full px-3 py-2 text-left text-sm bg-gray-50 border border-gray-300 rounded-md flex items-center justify-between hover:bg-gray-100 transition-colors"
                onClick={(e) => {
                  const dropdown = e.currentTarget.nextElementSibling;
                  if (dropdown) {
                    dropdown.classList.toggle('hidden');
                  }
                }}
              >
                <span className="truncate font-medium">{availablePlans.find(p => String(p.id) === String(selectedPlan))?.name || 'Select Plan'}</span>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>
              <div className="hidden absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {availablePlans.map(plan => (
                  <button
                    key={plan.id}
                    onClick={() => {
                      if (onPlanChange) onPlanChange(String(plan.id));
                      // Hide dropdown
                      const dropdown = document.querySelector('.absolute.z-10');
                      if (dropdown) dropdown.classList.add('hidden');
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 transition-colors ${String(plan.id) === String(selectedPlan) ? 'bg-blue-50 text-blue-700' : ''}`}
                  >
                    <div className="font-medium">{plan.name}</div>
                    {plan.type && <div className="text-xs text-gray-500">{plan.type}</div>}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-3 text-xs text-gray-500">
            No plans available
          </div>
        )}

        {/* Filters - Side by Side */}
        <div className="flex gap-2 mb-3">
          {/* Status Filter */}
          <div className="flex-1">
            <label className="text-xs text-gray-600 mb-1 block">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="in_review">In Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Type Filter */}
          <div className="flex-1">
            <label className="text-xs text-gray-600 mb-1 block">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="all">All</option>
              {uniqueTypes.map(type => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Items Summary */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Items to Review</h3>
          <p className="text-xs text-gray-500 mt-0.5">{filteredItems.length} of {items.length} items</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {filteredItems.map((item) => {
          const status = getItemStatus(item);
          const assetCounts = getAssetCounts(item.assets);
          const progress = getApprovalProgress(item);

          return (
            <button
              key={item.id}
              onClick={() => onItemSelect(item)}
              className={`w-full text-left p-3 rounded-lg border transition-all ${
                selectedItem?.id === item.id
                  ? 'bg-blue-50 border-blue-300 shadow-sm'
                  : 'bg-white hover:bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  {getItemIcon(item.type)}
                  <div className="flex-1">
                    <div className="font-medium text-sm text-gray-900 truncate" title={item.title}>
                      {item.title.length > 16 ? item.title.substring(0, 16) + '...' : item.title}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 capitalize">
                      {item.type}
                    </div>
                  </div>
                </div>
                {getStatusIcon(status)}
              </div>

              {/* Progress Bar */}
              {item.assets && item.assets.length > 0 && (
                <div className="mb-2">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span>Progress</span>
                    <span>{progress.approved}/{progress.total} approved</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-green-500 h-1.5 rounded-full transition-all"
                      style={{ width: `${progress.percentage}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Asset counts */}
              {Object.keys(assetCounts).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {Object.entries(assetCounts).map(([type, count]) => (
                    <span
                      key={type}
                      className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded"
                    >
                      {count} {type}
                    </span>
                  ))}
                </div>
              )}

              {/* Approval badges */}
              {item.approval && (
                <div className="flex items-center gap-1 mt-2">
                  {item.approval.am_status === 'approved' && (
                    <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                      AM ✓
                    </span>
                  )}
                  {item.approval.client_status === 'approved' && (
                    <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                      Client ✓
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default ItemsList;